import { GetReportsCostTypesData } from "../../../../services/accountService";
import AgGrid from "../../../common/AgGrid";
import { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from 'xlsx';  // ← ADDED IMPORT
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SuccessMessage from '../../../common/SuccessMessage';
import { useNavigate } from "react-router-dom";

// ===========================================================================
// SHARED FORMATTERS
// ===========================================================================
const dateFormatter = (params) => {
  if (!params.value) return "";
  const d = new Date(params.value);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const numberFormatter = (params) =>
  params.value != null && params.value !== ""
    ? Number(params.value).toLocaleString()
    : "";

const currencyFormatter = (params) => {
  if (params.value === null || params.value === undefined || params.value === "") return "";
  const n = Number(params.value);
  if (isNaN(n)) return "";
  const formatted = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (n < 0 ? '-$' : '$') + formatted;
};

const totalBoldRenderer = (params) => {
  const isTotal = params.data?.isTotalsRow;
  const value = params.valueFormatted ?? params.value ?? '';

  if (isTotal) {
    return <span style={{ fontWeight: 'bold' }}>{value}</span>;
  }

  return value;
};



// ===========================================================================
// COLUMN DEFS
// ===========================================================================
const getFinancialColumnDefs = (jobIdCellRenderer) => [
  { headerName: "Client ID", field: "clientId", minWidth: 230, filter: 'agTextColumnFilter', },
  {
    headerName: "Client Name", field: "clientName", minWidth: 380, filter: 'agTextColumnFilter',
    tooltipField: 'clientName',
    tooltipComponent: 'customTooltip',
    cellRenderer: totalBoldRenderer

  },
  {
    headerName: "Job ID",
    field: "jobId",
    minWidth: 220,
    filter: "agTextColumnFilter",
    cellRenderer: jobIdCellRenderer,
  },
  {
    headerName: "Job Name", field: "jobName", minWidth: 340, filter: 'agTextColumnFilter', tooltipField: 'jobName',
    tooltipComponent: 'customTooltip',
  },
  {
    headerName: "Mail Date", field: "mailDate", minWidth: 340, filter: 'agDateColumnFilter',   comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
    valueFormatter: p => {
      if (!p.value) return '';
      const d = new Date(p.value);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
  }, 
  {
    headerName: "Last Drop Date", field: "lastDropDate", minWidth: 230, filter: 'agDateColumnFilter',   comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
    valueFormatter: p => {
      if (!p.value) return '';
      const d = new Date(p.value);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
  },
  { headerName: "Accounting Status", field: "accountingStatus", minWidth: 230, filter: 'agTextColumnFilter' },
  { headerName: "Cost Status", field: "costStatus", minWidth: 230, filter: 'agTextColumnFilter' },
  {
    headerName: "Quantity Mailed", field: "qtyMailed", minWidth: 230, filter: 'agNumberColumnFilter', cellRenderer: totalBoldRenderer,
    valueFormatter: p => p.value != null ? Number(p.value).toLocaleString() : ''
  },
  { headerName: "Cost Category", field: "costCategory", minWidth: 230, filter: 'agTextColumnFilter' },
  { headerName: "Cost Type", field: "costType", minWidth: 230, filter: 'agTextColumnFilter' },
  {
    headerName: "Client Estimated Cost", field: "clntEstCost", minWidth: 280, filter: 'agNumberColumnFilter',
    cellRenderer: totalBoldRenderer,
    valueFormatter: p => {
      if (p.value == null || p.value === "") return '';
      const n = Number(p.value);
      if (isNaN(n)) return '';
      const formatted = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (n < 0 ? '-$' : '$') + formatted;
    }
  },
  {
    headerName: "Estimated Cost", field: "estCost", minWidth: 230, filter: 'agNumberColumnFilter', cellRenderer: totalBoldRenderer,
    valueFormatter: p => p.value != null ? '$' + Number(p.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
  },
  {
    headerName: "Actual Cost", field: "actCost", minWidth: 230, filter: 'agNumberColumnFilter', cellRenderer: totalBoldRenderer,
    valueFormatter: p => p.value != null ? '$' + Number(p.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
  },
  {
    headerName: "Variance", field: "variance", minWidth: 230, filter: 'agNumberColumnFilter', cellRenderer: totalBoldRenderer,
    valueFormatter: p => {
      if (p.value == null) return '';
      const n = Number(p.value);
      const formatted = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (n < 0 ? '-$' : '$') + formatted;
    }
  },
  { headerName: "Variance %", field: "variancePct", minWidth: 230, filter: 'agTextColumnFilter' },
  {
    headerName: "Total Cost", field: "totalCost", minWidth: 230, filter: 'agNumberColumnFilter', cellRenderer: totalBoldRenderer,
    valueFormatter: p => {
      if (p.value == null) return '';
      const n = Number(p.value);
      const formatted = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (n < 0 ? '-$' : '$') + formatted;
    }
  },
  {
    headerName: "Cost Per Piece", field: "costPerPiece", minWidth: 230, filter: 'agNumberColumnFilter', cellRenderer: totalBoldRenderer,
    valueFormatter: p => {
      if (p.value == null) return '';
      const n = Number(p.value);
      const formatted = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (n < 0 ? '-$' : '$') + formatted;
    }
  },
  {
    headerName: "Cost Per Thousand", field: "costPerThousand", minWidth: 230, filter: 'agNumberColumnFilter', cellRenderer: totalBoldRenderer,
    valueFormatter: p => {
      if (p.value == null) return '';
      const n = Number(p.value);
      const formatted = Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return (n < 0 ? '-$' : '$') + formatted;
    }
  },
  { headerName: "Vendor", field: "vendor", minWidth: 230, filter: 'agTextColumnFilter' },
  { headerName: "PO Number", field: "poNumber", minWidth: 230, filter: 'agTextColumnFilter' },
  { headerName: "Invoice Number", field: "invoiceNumber", minWidth: 250, filter: 'agTextColumnFilter' },
  // { headerName: "Invoice Date", field: "invoiceDate", minWidth: 230, filter: 'agTextColumnFilter' },
];

// ===========================================================================
// FIELD FORMAT GROUPS (shared by Excel + PDF exports so they stay consistent)
// ===========================================================================
const DATE_FIELDS = ['mailDate', 'lastDropDate', 'invoiceDate'];
const NUMBER_FIELDS = ['qtyMailed'];
const CURRENCY_FIELDS = ['clntEstCost', 'estCost', 'actCost', 'variance', 'totalCost', 'costPerPiece', 'costPerThousand'];
const PERCENT_FIELDS = ['variancePct'];

// Centralized cell formatter — one place so Excel and PDF render identically.
const formatValueForField = (field, value) => {
  if (value === null || value === undefined || value === '') return '';
  if (DATE_FIELDS.includes(field)) return dateFormatter({ value });
  if (NUMBER_FIELDS.includes(field)) return numberFormatter({ value });
  if (CURRENCY_FIELDS.includes(field)) return currencyFormatter({ value });
  if (PERCENT_FIELDS.includes(field)) return `${value}%`;
  return String(value);
};

// Which fields are numeric/currency → right-aligned in PDF.
const RIGHT_ALIGN_FIELDS = [...NUMBER_FIELDS, ...CURRENCY_FIELDS, ...PERCENT_FIELDS];

// ===========================================================================
// COMPONENT
// ===========================================================================
const FinancialReportTab = ({
  styles,
  clients,
  financialReportType,
  setFinancialReportType,
  searchText,
  setSearchText,
  onGridReady,
  costReportDataState,
  costReportFilters,
  setCostReportFilters,
  fetchCostReport,
  handleClearCostReport,
  jobDetailsJobId,
  fetchJobsByClient,
}) => {
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);
  const [costTypes, setCostTypes] = useState([]);

  const navigate = useNavigate()

  const jobIdCellRenderer = (params) => {
    if (!params.value || params.data?.isTotalsRow) {
      return params.value;
    }

    return (
      <span
        onClick={() =>
          navigate(`/job-details/${params.value}`, {
            state: {
              jobId: params.value,
              source: 'reports',

              // Financial report state
              reportFilters: costReportFilters,
              selectedClientId: selectedClientIds,
              reportType: financialReportType,
              rowData: costReportDataState,

              fromReports: true
            }
          })
        }
        style={{
          color: '#2563eb',
          textDecoration: 'underline',
          cursor: 'pointer'
        }}
      >
        {params.value}
      </span>
    );
  };

  const [showFilters, setShowFilters] = useState(true);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);  // ← ADDED
  const menuDropdownRef = useRef(null);  // ← ADDED

  // ✅ ref to access AgGrid's exposed methods (getVisibleColumns, etc.)
  //    Mirrors the JobsModule/JobReportsTab/VolumeTrackingTab pattern so
  //    drag/drop column visibility is respected on export.
  const agGridRef = useRef(null);

  // ✅ grid api ref — set in onGridReady (wrapped so the parent's own
  //    onGridReady callback still fires). Used to read selected/filtered
  //    rows for export at export time, exactly like JobsModule/
  //    JobReportsTab/ListAnalysisTab/VolumeTrackingTab's gridApiRef.
  const gridApiRef = useRef(null);

  // ✅ post-filter rows — mirrors CostSummary/ListSummaryTab/PackageCostsDetails/
  //    PostageDetails/BackendCostsDetails/ListsDetails/JobReportsTab/
  //    VolumeTrackingTab convention: null = no filter active (use all data),
  //    [] = filter matched nothing. (Previously this defaulted to [] for
  //    "no filter," which made a filter-matched-nothing state
  //    indistinguishable from "no filter at all" — the same class of bug
  //    that let stale/default data slip through export elsewhere in this
  //    app.)
  const [filteredData, setFilteredData] = useState(null);
  const [totals, setTotals] = useState({});

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');



  const toggleFilters = () => setShowFilters(!showFilters);
  const toggleMenuDropdown = () => setShowMenuDropdown(!showMenuDropdown);  // ← ADDED

  // JOB ID multi-select state
  const [jobSearch, setJobSearch] = useState("");
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const jobDropdownRef = useRef(null);

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm.trim()) return clients;
    return clients.filter(c =>
      c.clientName.toLowerCase().includes(clientSearchTerm.toLowerCase())
    );
  }, [clients, clientSearchTerm]);

  const parseNumber = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const num = Number(String(val).replace(/[$,]/g, ""));
    return isNaN(num) ? 0 : num;
  };
  useEffect(() => {
    
    setCostReportFilters((p) => ({
      ...p,
      jobIds: [],
    }));
  }, [selectedClientIds]);

  const calculateFinancialTotals = (data) => {
    let totals = {
      qtyMailed: 0,
      estCost: 0,
      actCost: 0,
      variance: 0,
      totalCost: 0,
      costPerPiece: 0,
      costPerThousand: 0,
    };

    const seenJobs = new Set();

    data.forEach((row) => {
      if (row?.isTotalsRow) return;

      // ✅ qtyMailed counted once per job
      if (!seenJobs.has(row.jobId)) {
        seenJobs.add(row.jobId);
        totals.qtyMailed += parseNumber(row.qtyMailed);
      }

      totals.estCost += parseNumber(row.estCost);
      totals.actCost += parseNumber(row.actCost);
      totals.variance += parseNumber(row.variance);
      totals.totalCost += parseNumber(row.totalCost);
    });

    // ✅ APPLY FORMULA HERE
    if (totals.qtyMailed > 0) {
      totals.costPerPiece = totals.totalCost / totals.qtyMailed;
      totals.costPerThousand = (totals.totalCost / totals.qtyMailed) * 1000;
    } else {
      totals.costPerPiece = 0;
      totals.costPerThousand = 0;
    }

    return totals;
  };

 
  const handleFilterChanged = (params) => {
    const api = params?.api || gridApiRef.current;
    if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

    const allCount = (costReportDataState || []).filter((r) => !r?.isTotalsRow).length;
    const filteredRows = [];
    api.forEachNodeAfterFilter((node) => {
      if (node.data && !node.data.isTotalsRow) filteredRows.push(node.data);
    });

    if (filteredRows.length < allCount) {
      setFilteredData(filteredRows);
    } else {
      setFilteredData(null);
    }
  };

  const totalsRow = {
    isTotalsRow: true,

    clientId: "",
    clientName: "TOTAL",
    jobId: "",
    jobName: "",

    mailDate: "",
    lastDropDate: "",

    accountingStatus: "",
    costStatus: "",

    qtyMailed: totals.qtyMailed,
    clntEstCost: "",
    // clntEstCost: totals.clntEstCost,
    estCost: totals.estCost,
    actCost: totals.actCost,
    variance: totals.variance,
    totalCost: totals.totalCost,
    costPerPiece: totals.costPerPiece,
    costPerThousand: totals.costPerThousand,

    costCategory: "",
    costType: "",
    variancePct: "",
    vendor: "",
    poNumber: "",
    invoiceNumber: "",
    invoiceDate: "",
  };

  const defaultColDef = {
    cellStyle: (params) => {
      const isTotal = params.node.rowPinned === "bottom";

      return {
        fontWeight: isTotal ? "bold" : "normal",
        backgroundColor: isTotal ? "#d3eaff" : undefined,
      };
    }
  };



  const baseCellStyle = (params) => {
    const isTotal =
      params.data?.isTotalsRow || params.node.rowPinned === "bottom";

    return {
      fontWeight: isTotal ? "bold" : "normal",
      backgroundColor: isTotal ? "#d3eaff" : undefined,
    };
  };




  // ✅ Checks `filteredData !== null` (not `.length > 0`) so a filter that
  // matches zero rows correctly shows zero totals instead of silently
  // falling back to the full, unfiltered totals.
  useEffect(() => {
    const sourceData =
      filteredData !== null ? filteredData : (costReportDataState || []);

    setTotals(calculateFinancialTotals(sourceData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costReportDataState, filteredData]);

  // ✅ Drop any stale filter snapshot whenever a fresh report is fetched
  // (mirrors the other cost/report tabs) — otherwise a filter applied to
  // the previous dataset could leak into a newly-run report.
  useEffect(() => {
    setFilteredData(null);
  }, [costReportDataState]);


  // Filter jobs based on search term
  const filteredJobs = useMemo(() => {
    return (jobDetailsJobId || []).filter((j) =>
      String(j.jobCode ?? "").toLowerCase().includes(jobSearch.toLowerCase())
    );
  }, [jobDetailsJobId, jobSearch]);

  useEffect(() => {
    if (!clients?.length) return;

    const ids = costReportFilters?.clientIds || [];

    if (!ids.length) return;

    const selected = clients.filter(c =>
      ids.includes(c.clientID)
    );

    setSelectedClients(selected);
    setSelectedClientIds(ids);

  }, [clients, costReportFilters?.clientIds]);

  useEffect(() => {
    const ids = costReportFilters?.clientIds || [];

    if (ids.length) {
      fetchJobsByClient(ids);
    }
  }, [costReportFilters?.clientIds]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
      if (jobDropdownRef.current && !jobDropdownRef.current.contains(e.target)) {
        setShowJobDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ CLOSE MENU ON OUTSIDE CLICK - ADDED
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
        setShowMenuDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load cost types
  useEffect(() => {
    const loadCostTypes = async () => {
      try {
        const res = await GetReportsCostTypesData();
        setCostTypes(res?.data || []);
      } catch (err) {
        console.error("Failed to load cost types", err);
      }
    };
    loadCostTypes();
  }, []);

  // Update costReportFilters whenever selectedClientIds change
  useEffect(() => {
    if (!selectedClientIds?.length) return;

    setCostReportFilters((prev) => ({
      ...prev,
      clientIds: selectedClientIds
    }));
  }, [selectedClientIds]);

  // Handle client selection/deselection
  const handleClientChange = (client) => {
    const id = client.clientID;

    setSelectedClients(prev => {
      let updated;

      if (prev.some(c => c.clientID === id)) {
        updated = prev.filter(c => c.clientID !== id);
      } else {
        updated = [...prev, client];
      }

      const ids = updated.map(c => c.clientID);

      setSelectedClientIds(ids);
      fetchJobsByClient?.(ids);

      // ✅ CRITICAL FIX — reset job IDs
      setCostReportFilters((p) => ({
        ...p,
        jobIds: [],
      }));

      return updated;
    });

    setClientSearchTerm("");
    setShowClientDropdown(false);
  };

  // Select all clients
  const handleSelectAll = () => {
    setSelectedClients(clients);

    const ids = clients.map(c => c.clientID);
    setSelectedClientIds(ids);

    fetchJobsByClient?.(ids);

    // ✅ RESET jobs
    setCostReportFilters((p) => ({
      ...p,
      jobIds: [],
    }));

    setClientSearchTerm("");
  };

  // Clear all selected clients
  const handleClearAll = () => {
    setSelectedClients([]);
    setSelectedClientIds([]);

    fetchJobsByClient?.([]);

    // ✅ CLEAR jobs
    setCostReportFilters((p) => ({
      ...p,
      jobIds: [],
    }));

    setClientSearchTerm("");
  };

  // Handle Clear button for Cost Report
  const handleClearCostReportFilters = () => {
    handleClearAll();
    setCostReportFilters({
      clientIds: [],
      jobIds: [],
      mailStartDate: "",
      mailEndDate: "",
      estVsActual: "",
      vendor: "",
      costTypeIds: ""
    });
    handleClearCostReport();
  };

  // ------------------------------------------------------------------
  // ✅ Helper: get the list of column field/headerName pairs that should
  // appear in an export. Uses AgGrid's getVisibleColumns() so any column
  // the user dragged off the grid is automatically excluded. Falls back
  // to the full column list ONLY when the grid api genuinely isn't
  // available yet.
  //
  // Previously this component built `columnsToExport` directly from the
  // static column def list every time, never consulting the live grid at
  // all — meaning hiding a column via the column tool panel had zero
  // effect on export. Brought in line with the rest of the app the same
  // way CostSummary/PackageCostsDetails/PostageDetails/
  // BackendCostsDetails/ListsDetails/JobReportsTab/VolumeTrackingTab were
  // fixed: trust the array whenever the api actually responds, even if
  // it's empty.
  // ------------------------------------------------------------------
  const getExportColumns = () => {
    const columnDefs = getFinancialColumnDefs(jobIdCellRenderer);

    const fallback = columnDefs
      .filter((col) => col.field && col.field !== 'action')
      .map((col) => ({ field: col.field, headerName: col.headerName || col.field }));

    if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
      const visible = agGridRef.current.getVisibleColumns();
      if (Array.isArray(visible)) {
        return visible.filter((c) => c.field && c.field !== 'action');
      }
    }
    return fallback;
  };

  // ------------------------------------------------------------------
  // ✅ Helper: only real data rows from a given source (excludes the
  // totals placeholder). Reusable for either the full or filtered scope.
  // ------------------------------------------------------------------
  const getExportableRowsFrom = (source) => (source || []).filter((r) => !r?.isTotalsRow);

  // ------------------------------------------------------------------
  // ✅ getExportRows — mirrors JobsModule/JobReportsTab/ListAnalysisTab/
  // VolumeTrackingTab's full three-tier scope rule (checked/"selected"
  // rows take priority over the active grid filter, which takes priority
  // over "everything"), computed live from the grid api at export time:
  //
  //   • Compute the rows AG Grid is showing after any column filter /
  //     quick-search (forEachNodeAfterFilter) — the "visible" set.
  //   • Of the checked rows (getSelectedRows), keep only those still
  //     visible -> "checkedVisible".
  //   1. If checkedVisible is non-empty -> export those.
  //   2. Else if a filter is active (visible !== all) -> export all
  //      visible, even if that's zero — never silently falls back to the
  //      full dataset.
  //   3. Else -> export everything.
  //
  // Returns { rows, scope } where scope is 'selected' | 'filtered' | 'all'.
  // ------------------------------------------------------------------
  const getExportRows = () => {
    const api = gridApiRef.current;
    const allRows = getExportableRowsFrom(costReportDataState);

    let visible = null;
    if (api && typeof api.forEachNodeAfterFilter === 'function') {
      const rows = [];
      api.forEachNodeAfterFilter((node) => {
        if (node.data && !node.data.isTotalsRow) rows.push(node.data);
      });
      visible = rows;
    }

    const isFiltered = visible !== null && visible.length !== allRows.length;

    let selected = [];
    if (api && typeof api.getSelectedRows === 'function') {
      selected = api.getSelectedRows().filter((r) => r && !r.isTotalsRow);
    }

    if (selected.length > 0) {
      if (visible !== null) {
        const visibleSet = new Set(visible);
        const checkedVisible = selected.filter((r) => visibleSet.has(r));
        if (checkedVisible.length > 0) {
          return { rows: checkedVisible, scope: 'selected' };
        }
      } else {
        return { rows: selected, scope: 'selected' };
      }
    }

    if (isFiltered) {
      return { rows: visible, scope: 'filtered' };
    }

    if (allRows.length === 0) {
      return { rows: [], scope: 'all' };
    }
    return {
      rows: visible !== null ? visible : allRows,
      scope: 'all',
    };
  };

  // ------------------------------------------------------------------
  // ✅ Shared TOTAL row builder — used by both exportToExcel and
  // printToPDF so the totals mapping only lives in one place. `totals`
  // is already kept in sync with the active filter scope via the effect
  // above, so this correctly reflects whatever is actually being
  // exported when no explicit row selection is active.
  // ------------------------------------------------------------------
  const getTotalsRowExport = (columnsToExport) => {
    const out = {};
    columnsToExport.forEach(({ field, headerName }, idx) => {
      if (idx === 0) {
        out[headerName] = 'TOTAL';
        return;
      }
      let totalValue = '';
      if (field === 'qtyMailed') {
        totalValue = numberFormatter({ value: totals.qtyMailed });
      } else if (field === 'estCost') {
        totalValue = currencyFormatter({ value: totals.estCost });
      } else if (field === 'actCost') {
        totalValue = currencyFormatter({ value: totals.actCost });
      } else if (field === 'variance') {
        totalValue = currencyFormatter({ value: totals.variance });
      } else if (field === 'totalCost') {
        totalValue = currencyFormatter({ value: totals.totalCost });
      } else if (field === 'costPerPiece') {
        totalValue = currencyFormatter({ value: totals.costPerPiece });
      } else if (field === 'costPerThousand') {
        totalValue = currencyFormatter({ value: totals.costPerThousand });
      }
      out[headerName] = totalValue;
    });
    return out;
  };

  // ✅ EXPORT TO EXCEL FUNCTION (now respects column visibility, selection, and filter scope)
  const exportToExcel = () => {
    const { rows: exportRows } = getExportRows();

    if (!exportRows || exportRows.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowMenuDropdown(false);
      return;
    }

    try {
      const columnsToExport = getExportColumns();
      if (columnsToExport.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowMenuDropdown(false);
        return;
      }

      // Transform data with formatting
      const exportData = exportRows.map((row) => {
        const formattedRow = {};
        columnsToExport.forEach(({ field, headerName }) => {
          formattedRow[headerName] = formatValueForField(field, row[field]);
        });
        return formattedRow;
      });

      // ✅ ADD TOTALS ROW TO EXPORT
      exportData.push(getTotalsRowExport(columnsToExport));

      // Create Excel sheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-fit column widths
      const colWidths = [];
      Object.keys(exportData[0] || {}).forEach((key, index) => {
        const maxLength = Math.max(
          key.length,
          ...exportData.map((row) => {
            const value = row[key];
            return value ? String(value).length : 0;
          })
        );
        colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      const sheetName = 'Cost Report';
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename with date
      const currentDate = new Date();
      const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
      const fileName = `${sheetName}_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);
      setMessage('Export completed successfully!');
      setMessageType('success');
      setShowMenuDropdown(false);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setMessage('Error exporting data to Excel');
      setMessageType('error');
      setShowMenuDropdown(false);
    }
  };

  // ✅ PRINT TO PDF FUNCTION (styled header card + KPI bar, respects column
  // visibility + selection/filter scope — mirrors CostSummary/
  // PackageCostsDetails/PostageDetails/BackendCostsDetails/ListsDetails/
  // JobReportsTab/VolumeTrackingTab)
  const printToPDF = () => {
    const { rows: validData, scope } = getExportRows();

    if (!validData || validData.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowMenuDropdown(false);
      return;
    }

    try {
      const columnsToExport = getExportColumns();
      if (columnsToExport.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowMenuDropdown(false);
        return;
      }

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 20;
      const cardX = marginX;
      const cardY = 20;
      const cardWidth = pageWidth - marginX * 2;

      // ----------------------------------------------------------------
      // Header card content
      // ----------------------------------------------------------------
      const titleText = 'Cost Report'.toUpperCase();
      const subtitleText = 'FINANCIAL · COST REPORT';

      const currentDate = new Date();
      const reportDateStr = `${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

      // ----------------------------------------------------------------
      // Card dimensions
      // ----------------------------------------------------------------
      const headerBarHeight = 60;
      const statsBarHeight = 50;
      const detailsBarHeight = 22;
      const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

      // Brand colors
      const BLUE_DARK = [30, 75, 120];
      const BLUE_MID = [46, 111, 159];
      const BLUE_DEEP = [22, 58, 95];

      // ----------------------------------------------------------------
      // 1) Top header bar (dark blue)
      // ----------------------------------------------------------------
      doc.setFillColor(...BLUE_DARK);
      doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

      // Logo block on far left
      const logoW = 90;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(180, 200, 220);
      doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

      // Title
      const titleAreaX = cardX + logoW;
      const titleAreaW = cardWidth - logoW;
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + 28, { align: 'center' });

      // Subtitle
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(200, 215, 230);
      doc.text(subtitleText, titleAreaX + titleAreaW / 2, cardY + 44, { align: 'center' });

      // Report metadata (right side)
      const metaX = cardX + cardWidth - 12;
      doc.setFontSize(7);
      doc.setTextColor(220, 230, 240);
      doc.setFont('helvetica', 'normal');
      doc.text(`Report: ${reportDateStr}`, metaX, cardY + 16, { align: 'right' });
      doc.text(`Records: ${validData.length}`, metaX, cardY + 28, { align: 'right' });

      // ----------------------------------------------------------------
      // 2) KPI stats bar (mid blue)
      // ----------------------------------------------------------------
      const statsY = cardY + headerBarHeight;
      doc.setFillColor(...BLUE_MID);
      doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

      const stats = [
        { label: 'QTY MAILED', value: numberFormatter({ value: totals.qtyMailed }) || '0' },
        { label: 'EST. COST', value: currencyFormatter({ value: totals.estCost }) || '$0' },
        { label: 'ACT. COST', value: currencyFormatter({ value: totals.actCost }) || '$0' },
        { label: 'TOTAL COST', value: currencyFormatter({ value: totals.totalCost }) || '$0' },
        { label: 'VARIANCE', value: currencyFormatter({ value: totals.variance }) || '$0' },
      ];

      const statCellW = cardWidth / stats.length;
      stats.forEach((stat, i) => {
        const cellX = cardX + i * statCellW;

        if (i > 0) {
          doc.setDrawColor(80, 130, 170);
          doc.setLineWidth(0.5);
          doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
        }

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(200, 220, 235);
        doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(255, 255, 255);
        doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
      });

      // ----------------------------------------------------------------
      // 3) Details strip (deep blue, full width)
      // ----------------------------------------------------------------
      const detailsY = statsY + statsBarHeight;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text('COST REPORT DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

      const scopeLabel =
        scope === 'selected' ? 'Selected'
          : scope === 'filtered' ? 'Filtered'
            : 'All';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(200, 215, 230);
      doc.text(
        `${scopeLabel} Records: ${validData.length}  ·  Confidential`,
        cardX + cardWidth - 12,
        detailsY + detailsBarHeight / 2 + 3,
        { align: 'right' }
      );

      // ----------------------------------------------------------------
      // Table — starts just below the card
      // ----------------------------------------------------------------
      const tableStartY = cardY + cardHeight + 10;

      const head = [columnsToExport.map((c) => c.headerName)];

      const body = validData.map((row) =>
        columnsToExport.map(({ field }) => formatValueForField(field, row[field]))
      );

      const totalsObj = getTotalsRowExport(columnsToExport);
      const footerRow = columnsToExport.map(({ headerName }) => totalsObj[headerName] ?? '');

      autoTable(doc, {
        head,
        body,
        foot: [footerRow],
        startY: tableStartY,
        margin: { left: marginX, right: marginX },
        styles: {
          fontSize: 5,
          cellPadding: 3,
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: [46, 111, 159],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 5,
        },
        footStyles: {
          fillColor: [240, 248, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        didParseCell: (data) => {
          if (data.section === 'body' || data.section === 'foot') {
            const col = columnsToExport[data.column.index];
            if (col && RIGHT_ALIGN_FIELDS.includes(col.field)) {
              data.cell.styles.halign = 'right';
            }
          }
        },
        didDrawPage: () => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            `Page ${pageCurrent} of ${pageCount}`,
            pageWidth - marginX,
            pageHeight - 10,
            { align: 'right' }
          );
        },
      });

      doc.save(`Cost-Report-${new Date().toISOString().split('T')[0]}.pdf`);

      setMessage('Export completed successfully!');
      setMessageType('success');
      setShowMenuDropdown(false);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setMessage('Error exporting data to PDF');
      setMessageType('error');
      setShowMenuDropdown(false);
    }
  };

  // ✅ CALLBACK TO CLOSE MESSAGE
  const handleMessageClose = () => {
    setMessage('');
    setMessageType('success');
  };

  return (
    <>

      <SuccessMessage
        message={message}
        onClose={handleMessageClose}
        type={messageType}
      />

      {/* Report type dropdown */}
      <div style={{ marginTop: "20px", width: "250px" }}>
        <label style={{ fontWeight: "600", display: "block", marginBottom: "8px" }}>
          Select Report Type
        </label>
        <select
          style={styles.input}
          value={financialReportType}
          onChange={(e) => setFinancialReportType(e.target.value)}
        >
          <option>Cost Report</option>
        </select>
      </div>

      {/* Filters */}
      {financialReportType && (
        <div className="report-filter-container">
          <div className="filter-header" style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '0px 10px 0px 0px', alignItems: 'center',
            backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
          }}>
            <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
            <button
              onClick={toggleFilters}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', color: '#fff', padding: '2px 6px',
                transition: 'transform 0.2s ease',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <img src="../assets/upward-arrow-icon.png"
                alt={showFilters ? 'Hide filters' : 'Show filters'}
                style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
            </button>
          </div>


          {showFilters && (
            <>
              <div className="report-filter-body">
                <div className="report-filter-row">
                  {/* MULTI-SELECT CLIENT DROPDOWN */}
                  <div className="report-filter-field" style={{ position: 'relative', maxWidth: '350px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Client(s)
                    </label>

                    <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>
                      {/* INPUT BOX */}
                      <div
                        onClick={() => setShowClientDropdown(true)}
                        style={{
                          minHeight: "38px",
                          maxHeight: "100px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          padding: "0px 8px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          backgroundColor: "#fff",
                          alignItems: "center",
                          alignContent: "flex-start",
                          cursor: "text",
                          overflowY: "auto"
                        }}
                      >
                        {/* CHIPS */}
                        {selectedClients.length > 1 ? (
                          <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap" }}>
                            {selectedClients.length} clients selected
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearAll();
                              }}
                              style={{ marginLeft: 6, cursor: "pointer" }}
                            >
                              ×
                            </span>
                          </span>
                        ) : (
                          selectedClients.map((c) => (
                            <span
                              key={c.clientID}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                background: "#e9ecef",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                fontSize: "13px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {c.clientName}
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClientChange(c);
                                }}
                                style={{ marginLeft: 6, cursor: "pointer" }}
                              >
                                ×
                              </span>
                            </span>
                          ))
                        )}

                        {/* SEARCH */}
                        <input
                          type="text"
                          placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                          value={clientSearchTerm}
                          onChange={(e) => {
                            setClientSearchTerm(e.target.value);
                            setShowClientDropdown(true);
                          }}
                          style={{
                            flex: 1,
                            minWidth: selectedClients.length === 0 ? "120px" : "40px",
                            width: "40px",
                            border: "none",
                            outline: "none",
                            fontSize: "14px",
                            padding: 0
                          }}
                        />
                      </div>

                      {/* DROPDOWN */}
                      {showClientDropdown && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            width: "100%",
                            backgroundColor: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            marginTop: "4px",
                            zIndex: 9999,
                            maxHeight: "250px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            overflow: "hidden"
                          }}
                        >
                          {/* ACTIONS */}
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button style={{
                              background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                              padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                            }} onClick={handleSelectAll}>Select All</button>
                            <button style={{
                              background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                              padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                            }} onClick={handleClearAll}>Clear All</button>
                          </div>

                          {/* LIST */}
                          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                            {filteredClients.map((client) => (
                              <div
                                key={client.clientID}
                                onClick={() => handleClientChange(client)}
                                style={{
                                  padding: "8px",
                                  display: "flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  backgroundColor: selectedClients.some(c => c.clientID === client.clientID)
                                    ? "#e6f7ff"
                                    : "#fff"
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedClients.some(c => c.clientID === client.clientID)}
                                  readOnly
                                  style={{ marginRight: "6px" }}
                                />
                                <span style={{ fontSize: "12px" }}>  {client.clientName}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* MULTI-SELECT JOB ID DROPDOWN */}
                  <div className="report-filter-field">
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Job ID</label>
                    <div ref={jobDropdownRef} style={{ position: "relative", width: "100%" }}>
                      {/* INPUT BOX */}
                      <div
                        onClick={() => setShowJobDropdown(true)}
                        style={{
                          minHeight: "38px",
                          maxHeight: "100px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          padding: "0px 8px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          backgroundColor: "#fff",
                          alignItems: "center",
                          alignContent: "flex-start",
                          cursor: "text",
                          overflowY: "auto"
                        }}
                      >
                        {/* CHIPS */}
                        {costReportFilters.jobIds.length > 1 ? (
                          <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap" }}>
                            {costReportFilters.jobIds.length} jobs selected
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setCostReportFilters((p) => ({ ...p, jobIds: [] }));
                              }}
                              style={{ marginLeft: 6, cursor: "pointer" }}
                            >
                              ×
                            </span>
                          </span>
                        ) : (
                          (selectedClientIds.length ? jobDetailsJobId : [])
                            .filter((j) => costReportFilters.jobIds.includes(j.jobID))
                            .map((j) => (
                              <span
                                key={j.jobID}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: "#e9ecef",
                                  padding: "2px 6px",
                                  borderRadius: "3px",
                                  fontSize: "13px",
                                  whiteSpace: "nowrap"
                                }}
                              >
                                {j.jobCode}
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCostReportFilters((p) => ({
                                      ...p,
                                      jobIds: p.jobIds.filter((id) => id !== j.jobID)
                                    }));
                                  }}
                                  style={{ marginLeft: 6, cursor: "pointer" }}
                                >
                                  ×
                                </span>
                              </span>
                            ))
                        )}

                        {/* SEARCH */}
                        <input
                          type="text"
                          placeholder={costReportFilters.jobIds.length === 0 ? "Select jobs..." : ""}
                          value={jobSearch}
                          onChange={(e) => {
                            setJobSearch(e.target.value);
                            setShowJobDropdown(true);
                          }}
                          style={{
                            flex: 1,
                            minWidth: costReportFilters.jobIds.length === 0 ? "120px" : "40px",
                            width: "40px",
                            border: "none",
                            outline: "none",
                            fontSize: "14px",
                            padding: 0
                          }}
                        />
                      </div>

                      {/* DROPDOWN */}
                      {showJobDropdown && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            width: "100%",
                            backgroundColor: "#fff",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            marginTop: "4px",
                            zIndex: 9999,
                            maxHeight: "250px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            overflow: "hidden"
                          }}
                        >
                          {/* ACTIONS */}
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button style={{
                              background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F',
                              padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                            }} onClick={() => {
                              const allIds = filteredJobs.map((j) => j.jobID);
                              const merged = [...new Set([...costReportFilters.jobIds, ...allIds])];
                              setCostReportFilters((p) => ({ ...p, jobIds: merged }));

                            }}>Select All</button>
                            <button style={{
                              background: 'none', border: '1px solid #dc3545', color: '#dc3545',
                              padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                            }} onClick={() => {
                              setCostReportFilters((p) => ({ ...p, jobIds: [] }));
                            }}>Clear All</button>
                          </div>

                          {/* LIST */}
                          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                            {filteredJobs.length > 0 ? (
                              filteredJobs.map((job) => (
                                <div
                                  key={job.jobID}
                                  onClick={() => {
                                    const updated = costReportFilters.jobIds.includes(job.jobID)
                                      ? costReportFilters.jobIds.filter((id) => id !== job.jobID)
                                      : [...costReportFilters.jobIds, job.jobID];
                                    setCostReportFilters((p) => ({ ...p, jobIds: updated }));
                                    setJobSearch("");
                                  }}
                                  style={{
                                    padding: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    backgroundColor: costReportFilters.jobIds.includes(job.jobID) ? "#e6f7ff" : "#fff"
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={costReportFilters.jobIds.includes(job.jobID)}
                                    readOnly
                                    style={{ marginRight: "6px" }}
                                  />
                                  <span style={{ fontSize: "12px" }}>{job.jobCode}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No jobs found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* MAIL DATE — now wired directly to costReportFilters */}
                  <div className="mail-date-filter">
                    <label className="mail-date-filter-label">
                      Mail Date
                    </label>
                    <div className="">
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!costReportFilters.mailStartDate ? 'empty' : ''}`}
                          value={costReportFilters.mailStartDate}
                          onChange={(e) => {
                            const newFromDate = e.target.value;
                            if (costReportFilters.mailEndDate && newFromDate > costReportFilters.mailEndDate) {
                              return;
                            }
                            setCostReportFilters((p) => ({ ...p, mailStartDate: newFromDate }));
                          }}
                          max={costReportFilters.mailEndDate || undefined}
                          title="Start Date"
                        />
                      </div>
                      <span className="arrow-icon">→</span>
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!costReportFilters.mailEndDate ? 'empty' : ''}`}
                          value={costReportFilters.mailEndDate}
                          onChange={(e) => {
                            const newToDate = e.target.value;
                            if (costReportFilters.mailStartDate && newToDate < costReportFilters.mailStartDate) {
                              return;
                            }
                            setCostReportFilters((p) => ({ ...p, mailEndDate: newToDate }));
                          }}
                          min={costReportFilters.mailStartDate || undefined}
                          title="End Date"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="report-filter-field">
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Estimate vs Actual</label>
                    <select
                      value={costReportFilters.estVsActual}
                      onChange={(e) =>
                        setCostReportFilters((prev) => ({ ...prev, estVsActual: e.target.value }))
                      }
                    >
                      <option value="">All</option>
                      <option value="Estimate">Estimate</option>
                      <option value="Actual">Actual</option>
                    </select>
                  </div>
                  <div className="report-filter-field">
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Vendor</label>
                    <input
                      type="text"
                      placeholder="Vendor..."
                      value={costReportFilters.vendor}
                      onChange={(e) =>
                        setCostReportFilters((prev) => ({ ...prev, vendor: e.target.value }))
                      }
                    />
                  </div>
                  <div className="report-filter-field">
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Cost Type
                    </label>
                    <select
                      value={costReportFilters.costTypeIds}
                      onChange={(e) =>
                        setCostReportFilters((prev) => ({ ...prev, costTypeIds: e.target.value }))
                      }
                    >
                      <option value="">All</option>
                      {costTypes.map((ct) => (
                        <option key={ct.costTypeID} value={ct.costTypeID}>
                          {ct.costTypeName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="report-filter-row" style={{ justifyContent: "flex-end" }}>
                  <div className="report-filter-buttons">
                    {(() => {
                      const hasFilters =
                        selectedClients.length > 0 ||
                        costReportFilters.jobIds.length > 0 ||
                        !!costReportFilters.mailStartDate ||
                        !!costReportFilters.mailEndDate ||
                        !!costReportFilters.estVsActual ||
                        !!costReportFilters.vendor ||
                        !!costReportFilters.costTypeIds;

                      return (
                        <>
                          <button
                            className="report-btn-run"
                            onClick={fetchCostReport}
                            disabled={!hasFilters}
                            style={
                              !hasFilters
                                ? { opacity: 0.5, cursor: "not-allowed" }
                                : undefined
                            }
                          >
                            Run Report
                          </button>

                          <button
                            className="report-btn-clear"
                            onClick={handleClearCostReportFilters}
                            disabled={!hasFilters}
                            style={
                              !hasFilters
                                ? { opacity: 0.5, cursor: "not-allowed" }
                                : undefined
                            }
                          >
                            Clear
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      )}

      {/* ✅ EXPORT MENU BUTTON - ADDED */}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', marginRight: '20px' }}>
        <div style={{ position: 'relative' }} ref={menuDropdownRef}>
          <button className="job-details-menu-button" onClick={toggleMenuDropdown}>
            <span className="menu-dot"></span>
            <span className="menu-dot"></span>
            <span className="menu-dot"></span>
          </button>

          {showMenuDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: '200px',
              overflow: 'hidden'
            }}>
              {/* Export to Excel */}
              <div
                onClick={exportToExcel}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s',
                  fontSize: '14px',
                  color: '#333',
                  borderBottom: '1px solid #f0f0f0'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="16" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                Export to Excel
              </div>

              {/* Print To Pdf */}
              <div
                onClick={printToPDF}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s',
                  fontSize: '14px',
                  color: '#333'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                Print To PDF
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div style={{ marginTop: "20px" }}>
        <AgGrid
          ref={agGridRef}
          enableSorting={true}
          enableFilter={true}
          pagination={false}
          defaultColDef={defaultColDef}
          height="500px"
          quickFilterText={searchText}
          hideExportButton={true}
          hideSideBar={true}
          onQuickFilterChange={setSearchText}
          rowData={costReportDataState}

          onFilterChanged={handleFilterChanged}   // ✅ ADD

          pinnedBottomRowData={[totalsRow]}       // ✅ ADD

          columnDefs={getFinancialColumnDefs(jobIdCellRenderer)}
          onGridReady={(params) => {
            gridApiRef.current = params.api;
            if (typeof onGridReady === 'function') onGridReady(params);
          }}


          gridOptions={{

            // ✅ Row-selection checkboxes — mirrors JobsModule/JobReportsTab/
            // ListAnalysisTab/VolumeTrackingTab. The shared AgGrid wrapper
            // supplies the checkbox column automatically once rowSelection
            // is set. isRowSelectable excludes the pinned TOTAL row.
            suppressRowClickSelection: true,
            rowSelection: 'multiple',
            isRowSelectable: (params) => !params.data || !params.data.isTotalsRow,

            overlayNoRowsTemplate: `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-align: center;
    ">

      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">
        📋
      </div>

      <div style="
        font-size: 16px;
        color: #6c757d;
        font-weight: 500;
      ">
        No records found
      </div>

    </div>
      `,
            getRowStyle: (params) => {
              if (params.node.rowPinned === "bottom") {
                return {
                  fontWeight: "bold",
                  backgroundColor: "#d3eaff",
                };
              }
            },
          }}

          onRowClicked={(params) => console.log("Row clicked:", params.data)}
        />
      </div>
    </>
  );
};

export default FinancialReportTab;